import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Save, Loader2, Globe, Image, Link2, Mail, Share2, BarChart3, Type,
  Search, Home, FileText, ExternalLink, Cog, Workflow, CheckCircle2,
  Plug, MessageSquare, User, Calendar, Building2, AlignLeft,
  Plus, Trash2, ChevronUp, ChevronDown, GripVertical, Zap, Star,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';

/* ─── Tab definitions ─────────────────────────────────────────── */
const TAB_GROUPS = [
  {
    label: 'Content',
    tabs: [
      { id: 'hero',        label: 'Hero Section',      icon: Home },
      { id: 'stats',       label: 'Stats & Numbers',   icon: BarChart3 },
      { id: 'sections',    label: 'Section Text',       icon: Type },
      { id: 'inner_pages', label: 'Inner Pages',        icon: FileText },
    ],
  },
  {
    label: 'How It Works',
    tabs: [
      { id: 'how_it_works_text', label: 'How It Works Text', icon: Workflow },
    ],
  },
  {
    label: 'Compliance & Integrations',
    tabs: [
      { id: 'compliance_text',   label: 'Compliance Text',   icon: CheckCircle2 },
      { id: 'integrations_text', label: 'Integrations Text', icon: Plug },
    ],
  },
  {
    label: 'Navigation',
    tabs: [
      { id: 'nav', label: 'Navigation', icon: Link2 },
    ],
  },
  {
    label: 'Footer & Contact',
    tabs: [
      { id: 'footer',         label: 'Footer Links',     icon: Globe },
      { id: 'contact',        label: 'Contact Details',  icon: Mail },
      { id: 'contacts_inbox', label: 'Contact Inbox',    icon: MessageSquare },
    ],
  },
  {
    label: 'Social & SEO',
    tabs: [
      { id: 'social', label: 'Social Links', icon: Share2 },
      { id: 'seo',    label: 'SEO & Meta',   icon: Search },
    ],
  },
  {
    label: 'Page SEO',
    tabs: [
      { id: 'seo_about',        label: 'About',       icon: User },
      { id: 'seo_features',     label: 'Features',    icon: Zap },
      { id: 'seo_pricing',      label: 'Pricing',     icon: BarChart3 },
      { id: 'seo_blog',         label: 'Blog',        icon: AlignLeft },
      { id: 'seo_contact',      label: 'Contact',     icon: Mail },
      { id: 'seo_testimonials', label: 'Testimonials',icon: Star },
    ],
  },
  {
    label: 'General',
    tabs: [
      { id: 'general', label: 'General', icon: Cog },
    ],
  },
];

/* ─── Field component ─────────────────────────────────────────── */
function Field({ label, value, onChange, placeholder = '', multiline = false, rows = 3, mono = false, hint, type = 'text' }) {
  const cls = clsx(
    'w-full px-4 py-3 text-sm border border-slate-200 rounded-xl transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400',
    mono && 'font-mono text-xs'
  );
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      {hint && <p className="text-xs text-slate-400 mb-2 leading-snug">{hint}</p>}
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={onChange}
          rows={rows}
          className={`${cls} resize-none`}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={onChange}
          className={cls}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

/* ─── SectionCard ─────────────────────────────────────────────── */
function SectionCard({ title, description, children, onSave, onPublish, saving, saveLabel = 'Save Changes' }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {(title || description) && (
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          {title && <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{title}</h2>}
          {description && <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{description}</p>}
        </div>
      )}
      <div className="p-6 space-y-5">
        {children}
      </div>
      {onSave && (
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
          <p className="text-xs text-slate-400">Changes are saved immediately to the live website</p>
          <div className="flex items-center gap-2">
            {onPublish && (
              <button onClick={onPublish} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-300 rounded-xl hover:bg-teal-50 transition-colors disabled:opacity-60">
                <Zap size={13} />
                Publish Now
              </button>
            )}
            <button
              onClick={onSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-60 shadow-sm"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : saveLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sub-heading divider ─────────────────────────────────────── */
function SubHeading({ children }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">{children}</p>
      <div className="flex-1 h-px bg-slate-100" />
    </div>
  );
}

/* ─── JSON array helpers ──────────────────────────────────────── */
function parseSafe(str, fallback = []) {
  try { const v = JSON.parse(str); return Array.isArray(v) ? v : fallback; }
  catch { return fallback; }
}

const INPUT_CLS = 'flex-1 min-w-0 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400 transition-colors';

/* Generic {label, href} list editor */
function LinkListEditor({ label, value, onChange, placeholder = '/page' }) {
  const items = parseSafe(value, []);

  function emit(next) { onChange(JSON.stringify(next)); }
  function update(i, field, val) {
    const next = items.map((it, idx) => idx === i ? { ...it, [field]: val } : it);
    emit(next);
  }
  function add() { emit([...items, { label: '', href: '' }]); }
  function remove(i) { emit(items.filter((_, idx) => idx !== i)); }
  function move(i, dir) {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    emit(next);
  }

  return (
    <div>
      {label && <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>}
      <div className="space-y-2 mb-3">
        {items.length === 0 && (
          <p className="text-xs text-slate-400 italic py-2 pl-1">No links yet — click Add Link to start</p>
        )}
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl p-2 border border-slate-100">
            <GripVertical size={14} className="text-slate-300 shrink-0" />
            <input value={item.label || ''} onChange={e => update(i, 'label', e.target.value)}
              className={INPUT_CLS} placeholder="Label (e.g. Features)" />
            <input value={item.href || ''} onChange={e => update(i, 'href', e.target.value)}
              className={INPUT_CLS} placeholder={placeholder} />
            <div className="flex gap-0.5 shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0}
                className="p-1 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30 transition-colors">
                <ChevronUp size={13} />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1}
                className="p-1 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30 transition-colors">
                <ChevronDown size={13} />
              </button>
              <button onClick={() => remove(i)}
                className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-400 transition-colors">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={add}
        className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 px-3 py-1.5 rounded-lg border border-dashed border-teal-300 hover:border-teal-400 hover:bg-teal-50/50 transition-all">
        <Plus size={12} /> Add Link
      </button>
    </div>
  );
}

const NAV_PRODUCT_ICONS = ['Brain', 'Calendar', 'DollarSign', 'Heart', 'Shield', 'Smartphone', 'Users', 'FileText', 'Zap', 'Globe', 'BarChart3', 'CheckCircle'];

/* {icon, label, desc, href} list editor for mega-menu */
function NavProductEditor({ value, onChange }) {
  const items = parseSafe(value, []);

  function emit(next) { onChange(JSON.stringify(next)); }
  function update(i, field, val) {
    emit(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it));
  }
  function add() { emit([...items, { icon: 'Zap', label: '', desc: '', href: '' }]); }
  function remove(i) { emit(items.filter((_, idx) => idx !== i)); }
  function move(i, dir) {
    const next = [...items]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]]; emit(next);
  }

  return (
    <div>
      <div className="space-y-3 mb-3">
        {items.length === 0 && (
          <p className="text-xs text-slate-400 italic py-2 pl-1">No items yet</p>
        )}
        {items.map((item, i) => (
          <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-100 space-y-2">
            <div className="flex items-center gap-2">
              <GripVertical size={14} className="text-slate-300 shrink-0" />
              <select value={item.icon || 'Zap'} onChange={e => update(i, 'icon', e.target.value)}
                className="px-2 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400">
                {NAV_PRODUCT_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
              <input value={item.label || ''} onChange={e => update(i, 'label', e.target.value)}
                className={INPUT_CLS} placeholder="Label (e.g. AI Features)" />
              <div className="flex gap-0.5 shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30"><ChevronUp size={13} /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30"><ChevronDown size={13} /></button>
                <button onClick={() => remove(i)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-400"><Trash2 size={13} /></button>
              </div>
            </div>
            <div className="flex gap-2 pl-5">
              <input value={item.desc || ''} onChange={e => update(i, 'desc', e.target.value)}
                className={INPUT_CLS} placeholder="Short description" />
              <input value={item.href || ''} onChange={e => update(i, 'href', e.target.value)}
                className={INPUT_CLS} placeholder="/features#ai" />
            </div>
          </div>
        ))}
      </div>
      <button onClick={add}
        className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 px-3 py-1.5 rounded-lg border border-dashed border-teal-300 hover:border-teal-400 hover:bg-teal-50/50 transition-all">
        <Plus size={12} /> Add Menu Item
      </button>
    </div>
  );
}

const VALUE_ICONS = ['Target', 'Heart', 'Shield', 'Users', 'Zap', 'Globe', 'Brain', 'CheckCircle', 'Star', 'Award', 'Lightbulb', 'Handshake'];

/* {icon, title, description} editor for About → Values */
function ValuesEditor({ value, onChange }) {
  const items = parseSafe(value, []);

  function emit(next) { onChange(JSON.stringify(next)); }
  function update(i, field, val) { emit(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it)); }
  function add() { emit([...items, { icon: 'Heart', title: '', description: '' }]); }
  function remove(i) { emit(items.filter((_, idx) => idx !== i)); }

  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">Values cards</label>
      <div className="space-y-3 mb-3">
        {items.length === 0 && <p className="text-xs text-slate-400 italic py-2 pl-1">No values added yet</p>}
        {items.map((item, i) => (
          <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-100 space-y-2">
            <div className="flex items-center gap-2">
              <select value={item.icon || 'Heart'} onChange={e => update(i, 'icon', e.target.value)}
                className="px-2 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-400/40">
                {VALUE_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
              <input value={item.title || ''} onChange={e => update(i, 'title', e.target.value)}
                className={INPUT_CLS} placeholder="Value title (e.g. Client First)" />
              <button onClick={() => remove(i)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-400 shrink-0"><Trash2 size={13} /></button>
            </div>
            <textarea value={item.description || ''} onChange={e => update(i, 'description', e.target.value)}
              rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400"
              placeholder="Short description of this value..." />
          </div>
        ))}
      </div>
      <button onClick={add}
        className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 px-3 py-1.5 rounded-lg border border-dashed border-teal-300 hover:border-teal-400 hover:bg-teal-50/50 transition-all">
        <Plus size={12} /> Add Value
      </button>
    </div>
  );
}

/* {year, event} editor for About → Milestones */
function MilestonesEditor({ value, onChange }) {
  const items = parseSafe(value, []);

  function emit(next) { onChange(JSON.stringify(next)); }
  function update(i, field, val) { emit(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it)); }
  function add() { emit([...items, { year: String(new Date().getFullYear()), event: '' }]); }
  function remove(i) { emit(items.filter((_, idx) => idx !== i)); }
  function move(i, dir) {
    const next = [...items]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]]; emit(next);
  }

  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">Milestones timeline</label>
      <div className="space-y-2 mb-3">
        {items.length === 0 && <p className="text-xs text-slate-400 italic py-2 pl-1">No milestones added yet</p>}
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl p-2 border border-slate-100">
            <GripVertical size={14} className="text-slate-300 shrink-0" />
            <input value={item.year || ''} onChange={e => update(i, 'year', e.target.value)}
              className="w-20 shrink-0 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400 font-mono"
              placeholder="2019" maxLength={4} />
            <input value={item.event || ''} onChange={e => update(i, 'event', e.target.value)}
              className={INPUT_CLS} placeholder="What happened this year..." />
            <div className="flex gap-0.5 shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30"><ChevronUp size={13} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 rounded hover:bg-slate-200 text-slate-400 disabled:opacity-30"><ChevronDown size={13} /></button>
              <button onClick={() => remove(i)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={add}
        className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 px-3 py-1.5 rounded-lg border border-dashed border-teal-300 hover:border-teal-400 hover:bg-teal-50/50 transition-all">
        <Plus size={12} /> Add Milestone
      </button>
    </div>
  );
}

/* string[] editor for Social Proof org names */
function StringListEditor({ label, value, onChange, placeholder = 'Item name' }) {
  const items = parseSafe(value, []);

  function emit(next) { onChange(JSON.stringify(next)); }
  function update(i, val) { emit(items.map((it, idx) => idx === i ? val : it)); }
  function add() { emit([...items, '']); }
  function remove(i) { emit(items.filter((_, idx) => idx !== i)); }

  return (
    <div>
      {label && <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>}
      <div className="space-y-2 mb-3">
        {items.length === 0 && <p className="text-xs text-slate-400 italic py-2 pl-1">No items yet</p>}
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={item} onChange={e => update(i, e.target.value)}
              className={INPUT_CLS} placeholder={placeholder} />
            <button onClick={() => remove(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-400 transition-colors shrink-0">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <button onClick={add}
        className="flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 px-3 py-1.5 rounded-lg border border-dashed border-teal-300 hover:border-teal-400 hover:bg-teal-50/50 transition-all">
        <Plus size={12} /> Add Item
      </button>
    </div>
  );
}

/* ─── Contact inbox card ──────────────────────────────────────── */
function ContactCard({ msg }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
            <User size={15} className="text-primary-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 truncate">{msg.name || 'Unknown'}</p>
            <p className="text-xs text-slate-500 truncate">{msg.email}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {msg.created_at && (
            <span className="flex items-center gap-1 text-[10px] text-slate-400">
              <Calendar size={10} />
              {new Date(msg.created_at).toLocaleDateString('en-AU', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </span>
          )}
          {msg.company && (
            <span className="flex items-center gap-1 text-[10px] text-slate-400">
              <Building2 size={10} />
              {msg.company}
            </span>
          )}
        </div>
      </div>

      {msg.message && (
        <div className="border-t border-slate-100 pt-3">
          <p
            className={clsx(
              'text-sm text-slate-600 leading-relaxed',
              !expanded && 'line-clamp-2'
            )}
          >
            {msg.message}
          </p>
          {msg.message.length > 120 && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="text-xs text-primary-600 font-semibold mt-1 hover:underline"
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────────────── */
export default function SiteSettings() {
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hero, setHero] = useState(null);
  const [settings, setSettings] = useState({});
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/marketing/hero`).then((r) => r.json()),
      fetch(`${API}/api/marketing/settings`).then((r) => r.json()),
    ])
      .then(([heroData, settingsData]) => {
        setHero(
          heroData || {
            headline: '', subheadline: '', cta_primary_text: 'Start Free Trial',
            cta_primary_url: 'https://app.rostery.com.au/register', cta_secondary_text: 'Watch Demo',
            cta_secondary_url: '#demo', badge_text: '', image_url: '', is_active: true,
            stat1_value: '', stat1_label: '', stat2_value: '', stat2_label: '',
            stat3_value: '', stat3_label: '', stat4_value: '', stat4_label: '',
          }
        );
        setSettings(settingsData || {});
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  // Load contacts when that tab is selected
  useEffect(() => {
    if (activeTab !== 'contacts_inbox') return;
    setContactsLoading(true);
    fetch(`${API}/api/marketing/contact-messages`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        // endpoint returns { messages, unread_count } — not a bare array
        const list = Array.isArray(data) ? data : (data?.messages || []);
        setContacts(list.slice(0, 10));
      })
      .catch(() => toast.error('Failed to load contact messages'))
      .finally(() => setContactsLoading(false));
  }, [activeTab]);

  async function saveHero() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/marketing/hero`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(hero),
      });
      if (!res.ok) throw new Error();
      toast.success('Hero section saved');
    } catch {
      toast.error('Failed to save hero');
    } finally {
      setSaving(false);
    }
  }

  async function saveSettings(keys) {
    setSaving(true);
    const subset = Object.fromEntries(keys.map((k) => [k, settings[k] ?? '']));
    try {
      const res = await fetch(`${API}/api/marketing/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(subset),
      });
      if (!res.ok) throw new Error();
      setSettings((prev) => ({ ...prev, ...subset }));
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  const h = (field) => (e) => setHero((v) => ({ ...v, [field]: e.target.value }));
  const s = (key) => (e) => setSettings((v) => ({ ...v, [key]: e.target.value }));
  const setS = (key, val) => setSettings((v) => ({ ...v, [key]: val }));

  async function publishNow(pagePath = '/') {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/marketing/revalidate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ secret: 'rostery-revalidate-2026', path: pagePath }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Published! Changes live on ${pagePath}`);
    } catch {
      toast.error('Publish failed — check marketing site is running');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-screen">

      {/* ── Left sidebar ─────────────────────────────────────────── */}
      <aside className="w-56 shrink-0 border-r border-slate-200 bg-white overflow-y-auto">
        <div className="p-4 border-b border-slate-100">
          <h1 className="text-sm font-bold text-slate-900">Site Settings</h1>
          <p className="text-xs text-slate-400 mt-0.5">Marketing website</p>
        </div>

        <nav className="p-2 space-y-0.5">
          {TAB_GROUPS.map((group, gi) => (
            <div key={gi} className={gi > 0 ? 'pt-3' : ''}>
              <p className="px-3 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {group.label}
              </p>
              {group.tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={clsx(
                      'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left',
                      isActive
                        ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-600 pl-2.5'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    )}
                  >
                    <Icon size={14} className="shrink-0" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* ── Content area ─────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto">
        {/* Content header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200 shadow-sm">
          <div>
            <h2 className="text-base font-bold text-slate-900">
              {TAB_GROUPS.flatMap((g) => g.tabs).find((t) => t.id === activeTab)?.label || 'Settings'}
            </h2>
            <p className="text-xs text-slate-400">ShiftCore Marketing Website</p>
          </div>
          <a
            href={settings.site_url || 'https://shiftcore.com.au'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
          >
            Visit Marketing Site <ExternalLink size={12} />
          </a>
        </div>

        <div className="p-6 space-y-6 max-w-3xl">

          {/* ── HERO ─────────────────────────────────────────────── */}
          {activeTab === 'hero' && hero && (
            <SectionCard
              title="Hero Section"
              description="The above-the-fold content visitors see first on the homepage"
              onSave={saveHero}
              onPublish={() => publishNow('/')}
              saving={saving}
            >
              <Field label="Badge Text" value={hero.badge_text} onChange={h('badge_text')} placeholder="Powered by ShiftCore" />
              <Field label="Headline" value={hero.headline} onChange={h('headline')} multiline rows={2}
                placeholder="NDIS Management Software Built for Modern Providers" />
              <Field label="Subheadline" value={hero.subheadline} onChange={h('subheadline')} multiline rows={3} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Primary CTA Text" value={hero.cta_primary_text} onChange={h('cta_primary_text')} placeholder="Start Free Trial" />
                <Field label="Primary CTA URL" value={hero.cta_primary_url} onChange={h('cta_primary_url')} />
                <Field label="Secondary CTA Text" value={hero.cta_secondary_text} onChange={h('cta_secondary_text')} />
                <Field label="Secondary CTA URL" value={hero.cta_secondary_url} onChange={h('cta_secondary_url')} />
              </div>
              <SubHeading>Hero Stats (4 counters shown under headline)</SubHeading>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Stat 1 — Value" value={hero.stat1_value} onChange={h('stat1_value')} placeholder="600+" />
                <Field label="Stat 1 — Label" value={hero.stat1_label} onChange={h('stat1_label')} placeholder="Providers" />
                <Field label="Stat 2 — Value" value={hero.stat2_value} onChange={h('stat2_value')} placeholder="14" />
                <Field label="Stat 2 — Label" value={hero.stat2_label} onChange={h('stat2_label')} placeholder="AI Features" />
                <Field label="Stat 3 — Value" value={hero.stat3_value} onChange={h('stat3_value')} placeholder="2.1M+" />
                <Field label="Stat 3 — Label" value={hero.stat3_label} onChange={h('stat3_label')} placeholder="Shifts Managed" />
                <Field label="Stat 4 — Value" value={hero.stat4_value} onChange={h('stat4_value')} placeholder="99.97%" />
                <Field label="Stat 4 — Label" value={hero.stat4_label} onChange={h('stat4_label')} placeholder="Uptime" />
              </div>
            </SectionCard>
          )}

          {/* ── STATS ────────────────────────────────────────────── */}
          {activeTab === 'stats' && (
            <SectionCard
              title="Site-wide Stats & Numbers"
              description="These values appear in the stats bar, CTA banners, pricing section, and testimonials page"
              onSave={() => saveSettings([
                'stat_providers', 'stat_providers_label', 'stat_shifts', 'stat_shifts_label',
                'stat_uptime', 'stat_uptime_label', 'stat_rating', 'stat_rating_label',
                'stat_ai_features', 'stat_satisfaction', 'trial_days', 'trial_cta',
              ])}
              onPublish={() => publishNow('/')}
              saving={saving}
            >
              <div className="grid grid-cols-2 gap-4">
                <Field label="Providers count" value={settings.stat_providers} onChange={s('stat_providers')} placeholder="600+" />
                <Field label="Providers label" value={settings.stat_providers_label} onChange={s('stat_providers_label')} placeholder="NDIS Providers" />
                <Field label="Shifts managed" value={settings.stat_shifts} onChange={s('stat_shifts')} placeholder="2.1M+" />
                <Field label="Shifts label" value={settings.stat_shifts_label} onChange={s('stat_shifts_label')} placeholder="Shifts Managed" />
                <Field label="Platform uptime" value={settings.stat_uptime} onChange={s('stat_uptime')} placeholder="99.97%" />
                <Field label="Uptime label" value={settings.stat_uptime_label} onChange={s('stat_uptime_label')} placeholder="Platform Uptime" />
                <Field label="Average rating" value={settings.stat_rating} onChange={s('stat_rating')} placeholder="4.9" />
                <Field label="Rating label" value={settings.stat_rating_label} onChange={s('stat_rating_label')} placeholder="Average Rating" />
                <Field label="AI Features count" value={settings.stat_ai_features} onChange={s('stat_ai_features')} placeholder="14" />
                <Field label="Satisfaction %" value={settings.stat_satisfaction} onChange={s('stat_satisfaction')} placeholder="97%" />
              </div>
              <SubHeading>Free Trial Banner</SubHeading>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Trial days" value={settings.trial_days} onChange={s('trial_days')} placeholder="14" />
                <Field label="Trial CTA button text" value={settings.trial_cta} onChange={s('trial_cta')} placeholder="Start 14-Day Free Trial" />
              </div>
            </SectionCard>
          )}

          {/* ── SECTIONS ─────────────────────────────────────────── */}
          {activeTab === 'sections' && (
            <SectionCard
              title="Homepage Section Text"
              description="Every heading, badge, and description text shown on the homepage"
              onSave={() => saveSettings([
                'section_features_badge', 'section_features_heading', 'section_features_description', 'section_features_tier3_heading',
                'section_split_badge', 'section_split_heading', 'section_split_description',
                'section_stats_heading',
                'section_testimonials_badge', 'section_testimonials_heading', 'section_testimonials_description',
                'section_pricing_badge', 'section_pricing_heading', 'section_pricing_description',
                'section_faq_badge', 'section_faq_heading', 'section_faq_description',
                'section_cta_heading', 'section_cta_secondary_text', 'section_cta_secondary_url', 'section_cta_footnote',
                'section_social_proof_label',
              ])}
              onPublish={() => publishNow('/')}
              saving={saving}
            >
              <SubHeading>Features Grid</SubHeading>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Badge" value={settings.section_features_badge} onChange={s('section_features_badge')} placeholder="Platform" />
                <div className="col-span-2">
                  <Field label="Heading" value={settings.section_features_heading} onChange={s('section_features_heading')} />
                </div>
              </div>
              <Field label="Description" value={settings.section_features_description} onChange={s('section_features_description')} multiline rows={2} />
              <Field label="Tier 3 checklist heading" value={settings.section_features_tier3_heading} onChange={s('section_features_tier3_heading')} placeholder="Plus everything else you need" />

              <SubHeading>Core Features Split</SubHeading>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Badge" value={settings.section_split_badge} onChange={s('section_split_badge')} placeholder="Core Capabilities" />
                <div className="col-span-2">
                  <Field label="Heading" value={settings.section_split_heading} onChange={s('section_split_heading')} />
                </div>
              </div>
              <Field label="Description" value={settings.section_split_description} onChange={s('section_split_description')} multiline rows={2} />

              <SubHeading>Stats Bar</SubHeading>
              <Field label="Heading (shown above stats)" value={settings.section_stats_heading} onChange={s('section_stats_heading')} placeholder="Trusted by Australia's leading providers" />

              <SubHeading>Testimonials</SubHeading>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Badge" value={settings.section_testimonials_badge} onChange={s('section_testimonials_badge')} placeholder="Testimonials" />
                <div className="col-span-2">
                  <Field label="Heading" value={settings.section_testimonials_heading} onChange={s('section_testimonials_heading')} />
                </div>
              </div>
              <Field label="Description" value={settings.section_testimonials_description} onChange={s('section_testimonials_description')} multiline rows={2} />

              <SubHeading>Pricing Preview</SubHeading>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Badge" value={settings.section_pricing_badge} onChange={s('section_pricing_badge')} placeholder="Pricing" />
                <div className="col-span-2">
                  <Field label="Heading" value={settings.section_pricing_heading} onChange={s('section_pricing_heading')} />
                </div>
              </div>
              <Field label="Description" value={settings.section_pricing_description} onChange={s('section_pricing_description')} multiline rows={2} />

              <SubHeading>FAQ Section</SubHeading>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Badge" value={settings.section_faq_badge} onChange={s('section_faq_badge')} placeholder="FAQ" />
                <div className="col-span-2">
                  <Field label="Heading" value={settings.section_faq_heading} onChange={s('section_faq_heading')} />
                </div>
              </div>
              <Field label="Description" value={settings.section_faq_description} onChange={s('section_faq_description')} multiline rows={2} />

              <SubHeading>CTA Banner</SubHeading>
              <Field label="Heading" value={settings.section_cta_heading} onChange={s('section_cta_heading')} placeholder="Ready to transform your NDIS operations?" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Secondary button text" value={settings.section_cta_secondary_text} onChange={s('section_cta_secondary_text')} placeholder="Talk to Sales" />
                <Field label="Secondary button URL" value={settings.section_cta_secondary_url} onChange={s('section_cta_secondary_url')} placeholder="/contact" />
              </div>
              <Field label="Footnote text" value={settings.section_cta_footnote} onChange={s('section_cta_footnote')} placeholder="No setup fees · Cancel anytime · Australian data storage" />

              <SubHeading>Social Proof Bar</SubHeading>
              <Field
                label="Label text"
                hint="Use {count} as a placeholder for the dynamic provider count"
                value={settings.section_social_proof_label}
                onChange={s('section_social_proof_label')}
                placeholder="Trusted by {count} NDIS providers across Australia"
              />
            </SectionCard>
          )}

          {/* ── INNER PAGES ──────────────────────────────────────── */}
          {activeTab === 'inner_pages' && (
            <SectionCard
              title="Inner Page Copy"
              description={<>All text shown on the Features, About, Contact, Testimonials, and Pricing pages. Use <code className="bg-slate-100 px-1 rounded font-mono text-xs">&#123;siteName&#125;</code> as a placeholder for the site name.</>}
              onSave={() => saveSettings([
                'page_features_hero_heading', 'page_features_hero_description', 'page_features_cta_heading',
                'page_features_stat_label_features', 'page_features_stat_label_ai', 'page_features_stat_label_providers', 'page_features_stat_label_satisfaction',
                'about_hero_heading', 'about_mission_heading', 'about_mission_body1', 'about_mission_body2',
                'about_values_heading', 'about_milestones_heading', 'about_cta_heading', 'about_contact_heading',
                'about_values', 'about_milestones',
                'contact_page_heading', 'contact_page_subheading', 'contact_info_heading',
                'contact_form_heading', 'contact_success_heading', 'contact_success_body',
                'page_testimonials_hero_heading', 'page_testimonials_hero_desc',
                'page_testimonials_reviews_heading', 'page_testimonials_reviews_desc', 'page_testimonials_cta_heading',
                'page_testimonials_cta_desc',
                'page_pricing_heading', 'page_pricing_description', 'page_pricing_annual_badge',
                'page_pricing_faqs_heading', 'page_pricing_toggle_monthly', 'page_pricing_toggle_annual',
                'split_included_label', 'split_live_badge', 'split_learn_more_text',
                'faq_all_label', 'faq_categories_label', 'pricing_view_full_text',
              ])}
              saving={saving}
            >
              <SubHeading>Features Page</SubHeading>
              <Field label="Hero heading" value={settings.page_features_hero_heading} onChange={s('page_features_hero_heading')} placeholder="Every tool your NDIS practice needs" />
              <Field label="Hero description" value={settings.page_features_hero_description} onChange={s('page_features_hero_description')} multiline rows={2} />
              <Field label="Bottom CTA heading" value={settings.page_features_cta_heading} onChange={s('page_features_cta_heading')} placeholder="Ready to see it in action?" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Stat label — Features" value={settings.page_features_stat_label_features} onChange={s('page_features_stat_label_features')} placeholder="Features" />
                <Field label="Stat label — AI Features" value={settings.page_features_stat_label_ai} onChange={s('page_features_stat_label_ai')} placeholder="AI Features" />
                <Field label="Stat label — Providers" value={settings.page_features_stat_label_providers} onChange={s('page_features_stat_label_providers')} placeholder="Providers" />
                <Field label="Stat label — Satisfaction" value={settings.page_features_stat_label_satisfaction} onChange={s('page_features_stat_label_satisfaction')} placeholder="Satisfaction" />
              </div>

              <SubHeading>About Page</SubHeading>
              <Field label="Hero heading" value={settings.about_hero_heading} onChange={s('about_hero_heading')} placeholder="Built by people who understand disability support" />
              <Field label="Mission heading" value={settings.about_mission_heading} onChange={s('about_mission_heading')} />
              <Field label="Mission paragraph 1" value={settings.about_mission_body1} onChange={s('about_mission_body1')} multiline rows={3} />
              <Field label="Mission paragraph 2 (use {siteName})" value={settings.about_mission_body2} onChange={s('about_mission_body2')} multiline rows={3} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Values section heading" value={settings.about_values_heading} onChange={s('about_values_heading')} placeholder="What we stand for" />
                <Field label="Timeline section heading" value={settings.about_milestones_heading} onChange={s('about_milestones_heading')} placeholder="How we got here" />
                <Field label="CTA heading (use {siteName})" value={settings.about_cta_heading} onChange={s('about_cta_heading')} />
                <Field label="Contact section heading" value={settings.about_contact_heading} onChange={s('about_contact_heading')} />
              </div>
              <ValuesEditor
                value={settings.about_values}
                onChange={(v) => setS('about_values', v)}
              />
              <MilestonesEditor
                value={settings.about_milestones}
                onChange={(v) => setS('about_milestones', v)}
              />

              <SubHeading>Contact Page</SubHeading>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Page heading" value={settings.contact_page_heading} onChange={s('contact_page_heading')} placeholder="Get in Touch" />
                <Field label="Info panel heading" value={settings.contact_info_heading} onChange={s('contact_info_heading')} placeholder="Contact Information" />
                <Field label="Form heading" value={settings.contact_form_heading} onChange={s('contact_form_heading')} placeholder="Send us a message" />
                <Field label="Success heading" value={settings.contact_success_heading} onChange={s('contact_success_heading')} placeholder="Message Sent!" />
              </div>
              <Field label="Page subheadline" value={settings.contact_page_subheading} onChange={s('contact_page_subheading')} multiline rows={2} />
              <Field label="Success state body" value={settings.contact_success_body} onChange={s('contact_success_body')} multiline rows={2} />

              <SubHeading>Testimonials Page</SubHeading>
              <Field label="Hero heading" value={settings.page_testimonials_hero_heading} onChange={s('page_testimonials_hero_heading')} placeholder="What providers say about ShiftCore" />
              <Field label="Hero description" value={settings.page_testimonials_hero_desc} onChange={s('page_testimonials_hero_desc')} multiline rows={2} />
              <Field label="Reviews section heading" value={settings.page_testimonials_reviews_heading} onChange={s('page_testimonials_reviews_heading')} placeholder="Stories from the field" />
              <Field label="Reviews description" value={settings.page_testimonials_reviews_desc} onChange={s('page_testimonials_reviews_desc')} multiline rows={2} />
              <Field label="Bottom CTA heading" value={settings.page_testimonials_cta_heading} onChange={s('page_testimonials_cta_heading')} placeholder="Join 600+ NDIS providers" />
              <Field label="Bottom CTA description" value={settings.page_testimonials_cta_desc} onChange={s('page_testimonials_cta_desc')} multiline rows={2} />

              <SubHeading>Pricing Page</SubHeading>
              <Field label="Page heading" value={settings.page_pricing_heading} onChange={s('page_pricing_heading')} placeholder="Simple, transparent pricing" />
              <Field label="Page description" value={settings.page_pricing_description} onChange={s('page_pricing_description')} multiline rows={2} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="Monthly toggle label" value={settings.page_pricing_toggle_monthly} onChange={s('page_pricing_toggle_monthly')} placeholder="Monthly" />
                <Field label="Annual toggle label" value={settings.page_pricing_toggle_annual} onChange={s('page_pricing_toggle_annual')} placeholder="Annual" />
                <Field label="Annual badge text" value={settings.page_pricing_annual_badge} onChange={s('page_pricing_annual_badge')} placeholder="SAVE 20%" />
              </div>
              <Field label="FAQs section heading" value={settings.page_pricing_faqs_heading} onChange={s('page_pricing_faqs_heading')} placeholder="Pricing FAQs" />

              <SubHeading>Shared UI Labels</SubHeading>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Feature split — included label" value={settings.split_included_label} onChange={s('split_included_label')} placeholder="Included in all plans" />
                <Field label="Feature split — live badge" value={settings.split_live_badge} onChange={s('split_live_badge')} placeholder="✓ Live" />
                <Field label="Feature split — learn more" value={settings.split_learn_more_text} onChange={s('split_learn_more_text')} placeholder="Learn more" />
                <Field label="FAQ — all questions label" value={settings.faq_all_label} onChange={s('faq_all_label')} placeholder="All Questions" />
                <Field label="FAQ — categories label" value={settings.faq_categories_label} onChange={s('faq_categories_label')} placeholder="Categories" />
                <Field label="Pricing — view full link" value={settings.pricing_view_full_text} onChange={s('pricing_view_full_text')} placeholder="View full pricing & comparison table" />
              </div>
            </SectionCard>
          )}

          {/* ── HOW IT WORKS TEXT ────────────────────────────────── */}
          {activeTab === 'how_it_works_text' && (
            <SectionCard
              title="How It Works Section"
              description="Edit the heading and sub-text for the How It Works section on the homepage"
              onSave={() => saveSettings(['how_it_works_heading', 'how_it_works_sub'])}
              saving={saving}
            >
              <Field
                label="Section Heading"
                value={settings.how_it_works_heading}
                onChange={s('how_it_works_heading')}
                placeholder="Get up and running in 3 simple steps"
              />
              <Field
                label="Section Sub-text"
                value={settings.how_it_works_sub}
                onChange={s('how_it_works_sub')}
                multiline
                rows={3}
                placeholder="Everything your team needs, set up in minutes — no complex onboarding, no IT department required."
              />
            </SectionCard>
          )}

          {/* ── COMPLIANCE TEXT ──────────────────────────────────── */}
          {activeTab === 'compliance_text' && (
            <SectionCard
              title="Compliance Section"
              description="Edit the heading, sub-text and body content for the Compliance section"
              onSave={() => saveSettings(['compliance_heading', 'compliance_sub', 'compliance_body'])}
              saving={saving}
            >
              <Field
                label="Section Heading"
                value={settings.compliance_heading}
                onChange={s('compliance_heading')}
                placeholder="Built for NDIS Compliance"
              />
              <Field
                label="Section Sub-text"
                value={settings.compliance_sub}
                onChange={s('compliance_sub')}
                multiline
                rows={2}
                placeholder="Stay audit-ready at all times with automated compliance checks and documentation."
              />
              <Field
                label="Body / Description"
                value={settings.compliance_body}
                onChange={s('compliance_body')}
                multiline
                rows={5}
                placeholder="ShiftCore is built from the ground up to meet NDIS Quality and Safeguards Commission requirements..."
              />
            </SectionCard>
          )}

          {/* ── INTEGRATIONS TEXT ────────────────────────────────── */}
          {activeTab === 'integrations_text' && (
            <SectionCard
              title="Integrations Section"
              description="Edit the heading and sub-text for the Integrations section"
              onSave={() => saveSettings(['integrations_heading', 'integrations_sub'])}
              saving={saving}
            >
              <Field
                label="Section Heading"
                value={settings.integrations_heading}
                onChange={s('integrations_heading')}
                placeholder="Connects with the tools you already use"
              />
              <Field
                label="Section Sub-text"
                value={settings.integrations_sub}
                onChange={s('integrations_sub')}
                multiline
                rows={3}
                placeholder="ShiftCore integrates seamlessly with Xero, Zapier, and leading NDIS plan management systems."
              />
            </SectionCard>
          )}

          {/* ── NAVIGATION ───────────────────────────────────────── */}
          {activeTab === 'nav' && (
            <SectionCard
              title="Navigation"
              description="CTA buttons, top nav links, and the product megamenu"
              onSave={() => saveSettings([
                'nav_cta_text', 'nav_cta_url', 'nav_signin_text', 'nav_signin_url',
                'nav_megamenu_title', 'nav_megamenu_desc', 'nav_product_links', 'nav_links',
              ])}
              onPublish={() => publishNow('/')}
              saving={saving}
            >
              <SubHeading>CTA Buttons</SubHeading>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Primary CTA text" value={settings.nav_cta_text} onChange={s('nav_cta_text')} placeholder="Start Free Trial" />
                <Field label="Primary CTA URL" value={settings.nav_cta_url} onChange={s('nav_cta_url')} placeholder="https://app.rostery.com.au/register" />
                <Field label="Sign In text" value={settings.nav_signin_text} onChange={s('nav_signin_text')} placeholder="Sign In" />
                <Field label="Sign In URL" value={settings.nav_signin_url} onChange={s('nav_signin_url')} placeholder="https://app.rostery.com.au/login" />
              </div>

              <SubHeading>Megamenu Panel</SubHeading>
              <Field label="Megamenu title" value={settings.nav_megamenu_title} onChange={s('nav_megamenu_title')} placeholder="Everything your team needs" />
              <Field label="Megamenu description" value={settings.nav_megamenu_desc} onChange={s('nav_megamenu_desc')} multiline rows={2} />

              <SubHeading>Top Nav Links</SubHeading>
              <LinkListEditor
                label="Nav links"
                value={settings.nav_links}
                onChange={(v) => setS('nav_links', v)}
                placeholder="/features"
              />

              <SubHeading>Product Megamenu Items</SubHeading>
              <NavProductEditor
                value={settings.nav_product_links}
                onChange={(v) => setS('nav_product_links', v)}
              />
            </SectionCard>
          )}

          {/* ── FOOTER LINKS ─────────────────────────────────────── */}
          {activeTab === 'footer' && (
            <SectionCard
              title="Footer Link Groups"
              description="Manage the link columns shown in the site footer"
              onSave={() => saveSettings([
                'footer_product_links', 'footer_company_links', 'footer_legal_links',
              ])}
              onPublish={() => publishNow('/')}
              saving={saving}
            >
              <LinkListEditor
                label="Product column"
                value={settings.footer_product_links}
                onChange={(v) => setS('footer_product_links', v)}
                placeholder="/features"
              />
              <LinkListEditor
                label="Company column"
                value={settings.footer_company_links}
                onChange={(v) => setS('footer_company_links', v)}
                placeholder="/about"
              />
              <LinkListEditor
                label="Legal column"
                value={settings.footer_legal_links}
                onChange={(v) => setS('footer_legal_links', v)}
                placeholder="/privacy"
              />
            </SectionCard>
          )}

          {/* ── CONTACT DETAILS ──────────────────────────────────── */}
          {activeTab === 'contact' && (
            <SectionCard
              title="Contact Details"
              description="Contact information shown throughout the site — footer, about page, contact page sidebar"
              onSave={() => saveSettings(['contact_email', 'contact_phone', 'contact_address'])}
              saving={saving}
            >
              <Field label="Email" value={settings.contact_email} onChange={s('contact_email')} placeholder="hello@shiftcore.com.au" />
              <Field label="Phone" value={settings.contact_phone} onChange={s('contact_phone')} placeholder="1300 SHIFTCORE" />
              <Field label="Office Address" value={settings.contact_address} onChange={s('contact_address')} placeholder="Level 12, 1 Martin Place, Sydney NSW 2000" />
            </SectionCard>
          )}

          {/* ── CONTACTS INBOX ───────────────────────────────────── */}
          {activeTab === 'contacts_inbox' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Contact Form Submissions</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Last 10 messages from the website contact form — read only</p>
                </div>
                {contacts.length > 0 && (
                  <span className="px-2.5 py-1 text-xs font-bold text-slate-600 bg-slate-100 rounded-full">
                    {contacts.length} messages
                  </span>
                )}
              </div>

              {contactsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
                  <MessageSquare size={36} className="mx-auto mb-3 text-slate-300" />
                  <p className="font-semibold text-slate-500">No messages yet</p>
                  <p className="text-sm text-slate-400 mt-1">Contact form submissions will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {contacts.map((msg, i) => (
                    <ContactCard key={msg.id || i} msg={msg} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── SOCIAL ───────────────────────────────────────────── */}
          {activeTab === 'social' && (
            <SectionCard
              title="Social Links"
              description="URLs shown in the footer and contact sections"
              onSave={() => saveSettings([
                'social_linkedin', 'social_twitter', 'social_facebook', 'social_instagram',
              ])}
              saving={saving}
            >
              {['social_linkedin', 'social_twitter', 'social_facebook', 'social_instagram'].map((key) => (
                <Field
                  key={key}
                  label={key.replace('social_', '').replace(/^\w/, (c) => c.toUpperCase())}
                  value={settings[key]}
                  onChange={s(key)}
                  placeholder="https://..."
                />
              ))}
            </SectionCard>
          )}

          {/* ── SEO ──────────────────────────────────────────────── */}
          {activeTab === 'seo' && (
            <SectionCard
              title="SEO & Metadata"
              description="Controls browser tab title, Google search snippet, and Open Graph (social sharing) previews"
              onSave={() => saveSettings([
                'meta_title', 'meta_description', 'meta_og_title', 'meta_og_description',
              ])}
              onPublish={() => publishNow('/')}
              saving={saving}
            >
              <Field label="Page title (browser tab)" value={settings.meta_title} onChange={s('meta_title')} placeholder="ShiftCore — The Most Advanced NDIS Platform in Australia" />
              <Field label="Meta description" value={settings.meta_description} onChange={s('meta_description')} multiline rows={3}
                placeholder="ShiftCore is the all-in-one NDIS management platform..." />
              <Field label="OG title (social shares)" value={settings.meta_og_title} onChange={s('meta_og_title')} placeholder="ShiftCore — The Most Advanced NDIS Platform" />
              <Field label="OG description" value={settings.meta_og_description} onChange={s('meta_og_description')} multiline rows={3} />
            </SectionCard>
          )}

          {/* ── PAGE SEO: ABOUT ──────────────────────────────────── */}
          {activeTab === 'seo_about' && (
            <SectionCard
              title="About Page SEO"
              description="Controls the browser tab title and search snippet for /about"
              onSave={() => saveSettings(['page_about_seo_title', 'page_about_seo_description'])}
              onPublish={() => publishNow('/about')}
              saving={saving}
            >
              <Field label="Page title (browser tab)" value={settings.page_about_seo_title} onChange={s('page_about_seo_title')} placeholder={`About — ${settings.site_name || 'ShiftCore'}`} />
              <Field label="Meta description" value={settings.page_about_seo_description} onChange={s('page_about_seo_description')} multiline rows={3} />
            </SectionCard>
          )}

          {/* ── PAGE SEO: FEATURES ───────────────────────────────── */}
          {activeTab === 'seo_features' && (
            <SectionCard
              title="Features Page SEO"
              description="Controls the browser tab title and search snippet for /features"
              onSave={() => saveSettings(['page_features_seo_title', 'page_features_seo_description'])}
              onPublish={() => publishNow('/features')}
              saving={saving}
            >
              <Field label="Page title (browser tab)" value={settings.page_features_seo_title} onChange={s('page_features_seo_title')} placeholder={`Features — ${settings.site_name || 'ShiftCore'}`} />
              <Field label="Meta description" value={settings.page_features_seo_description} onChange={s('page_features_seo_description')} multiline rows={3} />
            </SectionCard>
          )}

          {/* ── PAGE SEO: PRICING ────────────────────────────────── */}
          {activeTab === 'seo_pricing' && (
            <SectionCard
              title="Pricing Page SEO"
              description="Controls the browser tab title and search snippet for /pricing"
              onSave={() => saveSettings(['page_pricing_seo_title', 'page_pricing_seo_description'])}
              onPublish={() => publishNow('/pricing')}
              saving={saving}
            >
              <Field label="Page title (browser tab)" value={settings.page_pricing_seo_title} onChange={s('page_pricing_seo_title')} placeholder={`Pricing — ${settings.site_name || 'ShiftCore'}`} />
              <Field label="Meta description" value={settings.page_pricing_seo_description} onChange={s('page_pricing_seo_description')} multiline rows={3} />
            </SectionCard>
          )}

          {/* ── PAGE SEO: BLOG ───────────────────────────────────── */}
          {activeTab === 'seo_blog' && (
            <SectionCard
              title="Blog Page SEO"
              description="Controls the browser tab title and search snippet for /blog"
              onSave={() => saveSettings(['page_blog_seo_title', 'page_blog_seo_description'])}
              onPublish={() => publishNow('/blog')}
              saving={saving}
            >
              <Field label="Page title (browser tab)" value={settings.page_blog_seo_title} onChange={s('page_blog_seo_title')} placeholder={`Blog — ${settings.site_name || 'ShiftCore'}`} />
              <Field label="Meta description" value={settings.page_blog_seo_description} onChange={s('page_blog_seo_description')} multiline rows={3} />
            </SectionCard>
          )}

          {/* ── PAGE SEO: CONTACT ────────────────────────────────── */}
          {activeTab === 'seo_contact' && (
            <SectionCard
              title="Contact Page SEO"
              description="Controls the browser tab title and search snippet for /contact"
              onSave={() => saveSettings(['page_contact_seo_title', 'page_contact_seo_description'])}
              onPublish={() => publishNow('/contact')}
              saving={saving}
            >
              <Field label="Page title (browser tab)" value={settings.page_contact_seo_title} onChange={s('page_contact_seo_title')} placeholder={`Contact — ${settings.site_name || 'ShiftCore'}`} />
              <Field label="Meta description" value={settings.page_contact_seo_description} onChange={s('page_contact_seo_description')} multiline rows={3} />
            </SectionCard>
          )}

          {/* ── PAGE SEO: TESTIMONIALS ───────────────────────────── */}
          {activeTab === 'seo_testimonials' && (
            <SectionCard
              title="Testimonials Page SEO"
              description="Controls the browser tab title and search snippet for /testimonials"
              onSave={() => saveSettings(['page_testimonials_seo_title', 'page_testimonials_seo_description'])}
              onPublish={() => publishNow('/testimonials')}
              saving={saving}
            >
              <Field label="Page title (browser tab)" value={settings.page_testimonials_seo_title} onChange={s('page_testimonials_seo_title')} placeholder={`Testimonials — ${settings.site_name || 'ShiftCore'}`} />
              <Field label="Meta description" value={settings.page_testimonials_seo_description} onChange={s('page_testimonials_seo_description')} multiline rows={3} />
            </SectionCard>
          )}

          {/* ── GENERAL ──────────────────────────────────────────── */}
          {activeTab === 'general' && (
            <SectionCard
              title="General Site Info"
              description="Site name, URL, and footer content"
              onSave={() => saveSettings([
                'site_name', 'site_url', 'footer_tagline', 'footer_copyright', 'social_proof_logos',
              ])}
              saving={saving}
            >
              <div className="grid grid-cols-2 gap-4">
                <Field label="Site name" value={settings.site_name} onChange={s('site_name')} placeholder="ShiftCore" />
                <Field
                  label="Site URL"
                  value={settings.site_url}
                  onChange={s('site_url')}
                  placeholder="https://shiftcore.com.au"
                  hint="Used for social share links and canonical URLs"
                />
              </div>
              <Field label="Footer tagline" value={settings.footer_tagline} onChange={s('footer_tagline')} placeholder="Built by NDIS providers, for NDIS providers." />
              <Field label="Footer copyright text" value={settings.footer_copyright} onChange={s('footer_copyright')} placeholder="© 2026 ShiftCore Pty Ltd. All rights reserved." />
              <StringListEditor
                label="Social proof org names"
                value={settings.social_proof_logos}
                onChange={(v) => setS('social_proof_logos', v)}
                placeholder="Horizon Disability Services"
              />
            </SectionCard>
          )}

        </div>
      </main>
    </div>
  );
}
